import axiosInstance from './axios';
import axios from 'axios';

/**
 * Request a presigned URL from backend for uploading an image
 * @param fileExtension - File extension (jpg, png, jpeg)
 * @param folder - S3 folder path (default: 'collection-photos')
 * @returns Presigned URL data
 */
export const requestPresignedUrl = async (
    fileExtension = 'jpg',
    folder = 'collection-photos',
    bucket
) => {
    try {
        const response = await axiosInstance.post('/generate-upload-url', {
            fileExtension,
            folder,
            bucket
        });

        if (!response.data.success) {
            throw new Error(response.data.message || 'Failed to get presigned URL');
        }

        return response.data.data;
    } catch (error) {
        console.error('Error requesting presigned URL:', error);
        throw error;
    }
};

/**
 * Request multiple presigned URLs for batch upload
 * @param count - Number of URLs to generate
 * @param fileExtension - File extension
 * @param folder - S3 folder path
 * @returns Array of presigned URL data
 */
export const requestBatchPresignedUrls = async (
    count,
    fileExtension = 'jpg',
    folder = 'collection-photos'
) => {
    try {
        const response = await axiosInstance.post('/generate-batch-upload-urls', {
            count,
            fileExtension,
            folder,
        });

        if (!response.data.success) {
            throw new Error(response.data.message || 'Failed to get batch presigned URLs');
        }

        return response.data.data;
    } catch (error) {
        console.error('Error requesting batch presigned URLs:', error);
        throw error;
    }
};

/**
 * Upload an image file to S3 using presigned URL
 * @param presignedUrl - Presigned URL from backend
 * @param file - File or Blob to upload
 * @param contentType - Content type (default: 'image/jpeg')
 * @returns Promise that resolves when upload is complete
 */
export const uploadToS3 = async (
    presignedUrl,
    file,
    contentType = 'image/jpeg'
) => {
    try {
        await axios.put(presignedUrl, file, {
            headers: {
                'Content-Type': contentType,
            },
        });
    } catch (error) {
        console.error('Error uploading to S3:', error);
        throw error;
    }
};

/**
 * Convert base64 string to Blob
 * @param base64String - Base64 data URL or plain base64 string
 * @param contentType - Content type (default: 'image/jpeg')
 * @returns Blob object
 */
export const base64ToBlob = (base64String, contentType = 'image/jpeg') => {
    const base64Data = base64String.includes(',')
        ? base64String.split(',')[1]
        : base64String;

    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
};

/**
 * @param file - File or base64 string to upload
 * @param fileExtension - File extension
 * @param folder - S3 folder path
 * @returns S3 file URL
 */
export const uploadImageToS3 = async (
    file,
    fileExtension = 'jpg',
    folder = 'collection-photos',
    bucket
) => {
    try {
        const { uploadUrl, fileUrl } = await requestPresignedUrl(fileExtension, folder, bucket);
        let fileToUpload;
        let contentType = `image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}`;

        if (typeof file === 'string') {
            fileToUpload = base64ToBlob(file, contentType);
        } else {
            fileToUpload = file;
            contentType = file.type || contentType;
        }

        await uploadToS3(uploadUrl, fileToUpload, contentType);

        return fileUrl;
    } catch (error) {
        console.error('Error in complete S3 upload workflow:', error);
        throw error;
    }
};

/**
 * Get file extension from filename or base64 string
 * @param file - File object or base64 string
 * @returns File extension (jpg, png, etc.)
 */
export const getFileExtension = (file) => {
    if (typeof file === 'string') {
        if (file.startsWith('data:image/')) {
            const match = file.match(/data:image\/(\w+);/);
            return match ? match[1] : 'jpg';
        }
        return 'jpg';
    } else {
        const parts = file.name.split('.');
        return parts.length > 1 ? parts[parts.length - 1] : 'jpg';
    }
};

/**
 * Delete an image file from S3
 * @param {string} fileUrl 
 */
export const deleteImageFromS3 = async (fileUrl) => {
    try {
        const response = await axiosInstance.post('/delete-s3-file', { fileUrl });
        
        if (!response.data.success) {
            throw new Error(response.data.message || 'Failed to delete file from S3');
        }
        
        return response.data;
    } catch (error) {
        console.error('Error deleting from S3:', error);
        throw error;
    }
};